/**
 * Auth API: login, logout, session lifecycle.
 *
 * Used by: `authApp` / `authApi`; `version1|2|3`; OTP; `apiChannel` (via `authSessionApi`); shared consumers.
 */
import type { QueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { endpoints } from '../config/endpoints';
import { apiPost } from '../api/client';
import { getAuthStore } from './store';
import type { User } from '../../data/domains/profile';
import { bannersQueryKey, fetchBanners } from '../../data/domains/config';
import { clearAppConfigCache, fetchInitialConfig } from '../../data/domains/config';
import { ThemeService } from '../theme';
import { AUTH_TOKEN_KEY, CURRENT_USER_KEY } from './session/keys';
import { isWithinPostLoginGrace, resetPostLoginGrace } from './session/postLoginSignals';
import { signInWithPhonePassword } from './flows/phonePasswordSession';
import { getFcmDeviceTokenForAuth } from '../../platform/notifications/fcmDeviceToken';
import { clearFcmBackendSyncCache } from '../../platform/notifications/fcmBackendSync';
import { resetRootNavigationForAuth } from '../../navigation/navigationRootRef';
import { clearCustomerSessionCaches } from './clearCustomerSessionCaches';

let sessionExpiredHandler: (() => void) | null = null;

/** Re-export for callers that already import from this module (e.g. session helpers). */
export { setLoginJustHappened, resetPostLoginGrace } from './session/postLoginSignals';

export function setSessionExpiredHandler(fn: (() => void) | null): void {
  sessionExpiredHandler = fn;
}

async function hasPersistedSessionToken(): Promise<boolean> {
  if (getAuthStore().user?.session_token) {
    return true;
  }
  try {
    const token = await AsyncStorage.getItem(AUTH_TOKEN_KEY);
    return token != null && token.length > 0;
  } catch {
    return false;
  }
}

/**
 * Single path for session invalidation (401). Clears store first (sync) so UI updates immediately,
 * then clears session AsyncStorage so the next request does not send a stale token.
 * Does **not** clear saved login credentials used for biometric shortcut (`storedLoginCredentials`).
 * Skips if within POST_LOGIN_GRACE_MS of login (token may be persisted before zustand catches up).
 */
export async function clearSessionFromStorage(): Promise<void> {
  if (isWithinPostLoginGrace()) {
    return;
  }
  if (!(await hasPersistedSessionToken())) {
    return;
  }
  try {
    sessionExpiredHandler?.();
  } catch {
    // ignore so we still clear session
  }
  getAuthStore().clearUser();
  try {
    await AsyncStorage.multiRemove([AUTH_TOKEN_KEY, CURRENT_USER_KEY]);
  } catch {
    // ignore
  }
  await clearCustomerSessionCaches();
  clearAppConfigCache();
  if (queryClientRef) {
    await queryClientRef.clear();
  }
  await refetchPublicAppDataAfterSessionClear();
}

export async function login(phone: string, password: string): Promise<boolean> {
  const device_token = await getFcmDeviceTokenForAuth();
  const result = await signInWithPhonePassword(phone, password, { device_token });
  return result.ok;
}

let queryClientRef: QueryClient | null = null;

export function setQueryClient(client: QueryClient | null): void {
  queryClientRef = client;
}

/**
 * After `clearAppConfigCache()` and (on logout) `queryClient.clear()`, restore the same
 * public data the app loads at cold start: `initial` config, theme from org, and banners.
 * Keeps guest/unauthenticated screens from seeing empty `getOrganization()` / branding.
 */
async function refetchPublicAppDataAfterSessionClear(): Promise<void> {
  try {
    await fetchInitialConfig();
    await ThemeService.initialize();
    if (queryClientRef) {
      await queryClientRef.prefetchQuery({ queryKey: bannersQueryKey, queryFn: fetchBanners });
    }
  } catch {
    // Do not throw from auth teardown; UIs can use getConfigLoadError and retry.
  }
}

export async function logout(): Promise<void> {
  resetPostLoginGrace();
  const had_session = !!(await hasPersistedSessionToken());
  getAuthStore().clearUser();
  resetRootNavigationForAuth(false);
  await clearCustomerSessionCaches();
  if (had_session) {
    try {
      await apiPost(endpoints.logout);
    } catch {
      // ignore — local session is cleared below regardless
    }
  }
  try {
    await AsyncStorage.multiRemove([AUTH_TOKEN_KEY, CURRENT_USER_KEY]);
  } catch {
    // ignore
  }
  await clearFcmBackendSyncCache();
  try {
    const { clearDevApiCallHistory } =
      require('../../exDev/apiCallHistory/store') as typeof import('../../exDev/apiCallHistory/store');
    await clearDevApiCallHistory();
  } catch {
    // dev-only
  }
  clearAppConfigCache();
  if (queryClientRef) {
    await queryClientRef.clear();
  }
  await refetchPublicAppDataAfterSessionClear();
}

export async function clearLocalSessionOnly(): Promise<void> {
  resetPostLoginGrace();
  getAuthStore().clearUser();
  await clearFcmBackendSyncCache();
  try {
    await AsyncStorage.multiRemove([AUTH_TOKEN_KEY, CURRENT_USER_KEY]);
  } catch {
    // ignore
  }
}

/** Dev hub API target switch — clear local session without network logout or config refetch. */
export async function clearSessionForDevHubApiTargetSwitch(): Promise<void> {
  await clearLocalSessionOnly();
  await clearCustomerSessionCaches();
  clearAppConfigCache();
  if (queryClientRef) {
    await queryClientRef.clear();
  }
}

export async function loadStoredUser(): Promise<void> {
  try {
    const token = await AsyncStorage.getItem(AUTH_TOKEN_KEY);
    const userStr = await AsyncStorage.getItem(CURRENT_USER_KEY);
    if (token && userStr) {
      const user: User = JSON.parse(userStr);
      getAuthStore().setUser(user);
    } else {
      getAuthStore().clearUser();
    }
  } catch (e) {
    getAuthStore().clearUser();
  } finally {
    getAuthStore().setReady(true);
  }
}
